import * as React from "react";
import { Box, Button, Dialog, Flex, Switch, Text } from "@radix-ui/themes";
import { Id } from "@repo/convex/convex/_generated/dataModel";
import { useOptimisticArchiveItem } from "../../common/hooks.ts";
import { useMutation } from "convex/react";
import { api } from "@repo/convex/convex/_generated/api";
import { toast } from "sonner";
import { useQuery } from "convex-helpers/react/cache/hooks";
import { useEffect } from "react";
import { DialogCloseButton } from "../../common/DialogCloseButton.tsx";
import { ConnectedNotesEditor, SaveState } from "../../notes/editor/ConnectedNotesEditor.tsx";
import { SaveStateLabel } from "../../notes/editor/SaveStateLabel.tsx";
import { itemTitle } from "../../common/items/format.ts";

interface Props {
  itemId: Id<"items"> | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => unknown;
}

// "Tuck it in", like the app's archive sheet: your notes on it while it's fresh, whether the AI
// should learn from it (starting as Settings has it), then into the archive with an Undo.
export const ArchiveItemDialog: React.FC<Props> = ({ isOpen, itemId, setIsOpen }) => {
  const archive = useOptimisticArchiveItem();
  const unarchive = useMutation(api.items.unarchiveMine);
  const item = useQuery(api.items.findMine, itemId ? { itemId } : "skip");
  // Only while it's open: the dialog's always mounted, signed out too
  const settings = useQuery(api.userSettings.getMineOrDefault, isOpen ? {} : "skip");
  const [learn, setLearn] = React.useState<boolean | null>(null);
  const [saveState, setSaveState] = React.useState<SaveState>({ kind: "loading" });

  useEffect(() => {
    setLearn(null);
  }, [itemId]);

  const shouldLearn = learn ?? settings?.settings.shouldUpdateMyProfileWhenArchivingItem ?? false;

  const tuckIn = () => {
    setIsOpen(false);
    if (!item) return;
    archive({ itemId: item._id, updateProfile: shouldLearn })
      .then(() =>
        toast.success("Tucked in", {
          action: {
            label: "Undo",
            onClick: () => unarchive({ itemId: item._id }).catch(console.error),
          },
        }),
      )
      .catch(console.error);
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(isOpen) => {
        if (!itemId) return;
        setIsOpen(isOpen);
      }}
    >
      <Dialog.Content align={"start"} maxWidth="600px" style={{ marginTop: "6vh" }}>
        <Dialog.Title mb={"1"}>Tuck it in</Dialog.Title>
        <Dialog.Description color={"gray"} size={"2"} mb={"4"}>
          {item ? itemTitle(item) : "\u00a0"}
        </Dialog.Description>

        <Text as="div" size="2" weight="bold" mb="2">
          Your notes
        </Text>
        {isOpen ? <ConnectedNotesEditor itemId={itemId} onSaveStateChange={setSaveState} /> : null}
        <Flex justify="end" mt="1" style={{ minHeight: 18 }}>
          <SaveStateLabel state={saveState} />
        </Flex>

        <label className="dialog-switch-row">
          <Box flexGrow="1">
            <Text as="div" size="2" weight="bold">
              Let the AI learn from this
            </Text>
            <Text as="div" size="2" color="gray">
              Uses the article and your notes to update your profile.
            </Text>
          </Box>
          <Switch checked={shouldLearn} onCheckedChange={setLearn} />
        </label>

        <Flex gap="3" mt="5" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Not yet
            </Button>
          </Dialog.Close>
          <Button onClick={tuckIn} disabled={!item}>
            Tuck it in
          </Button>
        </Flex>
        <DialogCloseButton onClick={() => setIsOpen(false)} />
      </Dialog.Content>
    </Dialog.Root>
  );
};
